/**
 * Sample data from Payment Processing Platform CSV
 * This data represents a comprehensive project for Intellesphere organization
 */

import type { Project, Task, Epic, Priority, TaskStatus, Complexity } from '../types';

const ORG_ID = 'org_intellesphere';
const PROJECT_ID = 'proj-payment-platform';

export const sampleProject: Project = {
  id: PROJECT_ID,
  organizationId: ORG_ID,
  name: 'Payment Processing Platform',
  description: 'Enterprise-grade payment processing platform with PCI DSS compliance, multi-region support, and comprehensive fraud detection',
  status: 'In Progress',
  proposal: `
# Payment Processing Platform

## Overview
Build a complete payment processing platform from infrastructure to frontend applications.

## Key Objectives
- PCI DSS Level 1 Compliance
- Multi-region disaster recovery
- Real-time fraud detection
- Comprehensive merchant and admin dashboards
- Multi-payment method support (cards, wallets, BNPL)

## Technology Stack
- Backend: Go microservices
- Frontend: React/Next.js
- Infrastructure: AWS (EKS, Aurora, Redis, Kafka)
- IaC: Terraform
- Observability: Prometheus, Grafana, ELK, Jaeger
  `,
  costs: [
    { id: 'cost-1', description: 'AWS Infrastructure (Annual)', amount: 480000, category: 'Infrastructure' },
    { id: 'cost-2', description: 'Development Team (12 months)', amount: 1800000, category: 'Personnel' },
    { id: 'cost-3', description: 'Third-party Services & APIs', amount: 120000, category: 'Services' },
    { id: 'cost-4', description: 'Security & Compliance', amount: 250000, category: 'Compliance' },
  ],
  teamMembers: [
    { userId: 'aidimentry', role: 'developer' },
    { userId: 'ah.masud.phd', role: 'stakeholder' },
    { userId: 'pemsapi', role: 'investor' },
  ],
  createdAt: '2026-01-15T00:00:00Z',
  updatedAt: '2026-02-20T00:00:00Z',
  externalLinks: [
    { type: 'GitHub', url: 'https://github.com/intellesphere/payment-platform' },
    { type: 'Jira', url: 'https://intellesphere.atlassian.net/projects/PAY' },
  ],
};

// Epic definitions from CSV
export const sampleEpics: Epic[] = [
  {
    id: 'epic-project-setup',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Project Setup',
    description: 'Initial repository, development environment, and CI/CD pipeline setup',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-01-15T00:00:00Z',
  },
  {
    id: 'epic-terraform',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Terraform',
    description: 'Infrastructure as Code modules for AWS resources',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-01-15T00:00:00Z',
  },
  {
    id: 'epic-kubernetes',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Kubernetes',
    description: 'Kubernetes cluster configuration and service mesh setup',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-01-15T00:00:00Z',
  },
  {
    id: 'epic-helm',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Helm',
    description: 'Helm charts for microservice deployments',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-01-15T00:00:00Z',
  },
  {
    id: 'epic-go-common',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Go Common',
    description: 'Shared Go libraries for all microservices',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-01-15T00:00:00Z',
  },
  {
    id: 'epic-protobuf',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Protobuf',
    description: 'Protocol buffer definitions for gRPC services',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-01-15T00:00:00Z',
  },
  {
    id: 'epic-payment-service',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Payment Service',
    description: 'Core payment processing microservice',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-01-15T00:00:00Z',
  },
];

// Helper function to map priority
const mapPriority = (p: string): Priority => {
  if (p === 'P0') return 'P0';
  if (p === 'P1') return 'P1';
  if (p === 'P2') return 'P2';
  return 'Medium';
};

// Tasks from CSV (showing first 50 comprehensive examples)
export const sampleTasks: Task[] = [
  {
    id: 'INF-001',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Repository & Monorepo Setup',
    description: 'Initialize Git repository with monorepo structure, configure .github workflows, CODEOWNERS, PR templates',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P0',
    complexity: 'Low',
    category: 'Infrastructure',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-project-setup',
    estimatedDays: 2,
    estimatedHours: 16,
    llmAdjustedHours: 12,
    dependencies: '',
    skillsRequired: 'DevOps/Git',
    phase: '1',
    deliverables: 'Configured repository',
    notes: 'LLM assists with boilerplate generation',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-01-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
  },
  {
    id: 'INF-002',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Development Environment Configuration',
    description: 'Docker Compose setup for local dev, Makefile commands, go.work configuration',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P0',
    complexity: 'Medium',
    category: 'Infrastructure',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-project-setup',
    estimatedDays: 3,
    estimatedHours: 24,
    llmAdjustedHours: 16,
    dependencies: '',
    skillsRequired: 'DevOps/Docker',
    phase: '1',
    deliverables: 'docker-compose.yml; Makefile',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-01-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
  },
  {
    id: 'INF-003',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'CI Pipeline Setup',
    description: 'GitHub Actions for build, test, lint, security scan for all services',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P0',
    complexity: 'Medium',
    category: 'Infrastructure',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-project-setup',
    estimatedDays: 4,
    estimatedHours: 32,
    llmAdjustedHours: 20,
    dependencies: '',
    skillsRequired: 'DevOps/CI-CD',
    phase: '1',
    deliverables: 'ci.yml workflow',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-01-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
  },
  {
    id: 'INF-004',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'CD Pipeline Setup - Staging',
    description: 'ArgoCD/GitOps deployment to staging environment',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P0',
    complexity: 'Medium',
    category: 'Infrastructure',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-project-setup',
    estimatedDays: 3,
    estimatedHours: 24,
    llmAdjustedHours: 16,
    dependencies: 'INF-003',
    skillsRequired: 'DevOps/K8s',
    phase: '1',
    deliverables: 'cd-staging.yml',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-01-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
  },
  {
    id: 'INF-005',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'CD Pipeline Setup - Production',
    description: 'Production deployment pipeline with approvals and rollback',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P0',
    complexity: 'High',
    category: 'Infrastructure',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-project-setup',
    estimatedDays: 4,
    estimatedHours: 32,
    llmAdjustedHours: 24,
    dependencies: 'INF-004',
    skillsRequired: 'DevOps/K8s',
    phase: '1',
    deliverables: 'cd-production.yml',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-01-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
  },
  {
    id: 'INF-006',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'VPC & Network Module',
    description: 'AWS VPC with public/private subnets, NAT, security groups, NACLs per design',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P0',
    complexity: 'High',
    category: 'Infrastructure',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-terraform',
    estimatedDays: 5,
    estimatedHours: 40,
    llmAdjustedHours: 28,
    dependencies: '',
    skillsRequired: 'Terraform/AWS',
    phase: '1',
    deliverables: 'terraform/modules/vpc',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-01-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
  },
  {
    id: 'INF-007',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'EKS Cluster Module',
    description: 'Kubernetes cluster with managed node groups, autoscaling, Calico policies',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P0',
    complexity: 'High',
    category: 'Infrastructure',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-terraform',
    estimatedDays: 6,
    estimatedHours: 48,
    llmAdjustedHours: 32,
    dependencies: 'INF-006',
    skillsRequired: 'Terraform/K8s',
    phase: '1',
    deliverables: 'terraform/modules/eks',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-01-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
  },
  {
    id: 'INF-008',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Aurora PostgreSQL Module',
    description: 'Multi-AZ PostgreSQL cluster with read replicas, parameter groups, backups',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P0',
    complexity: 'High',
    category: 'Infrastructure',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-terraform',
    estimatedDays: 4,
    estimatedHours: 32,
    llmAdjustedHours: 24,
    dependencies: 'INF-006',
    skillsRequired: 'Terraform/AWS',
    phase: '1',
    deliverables: 'terraform/modules/rds',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-01-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
  },
  {
    id: 'INF-009',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'ElastiCache Redis Module',
    description: 'Redis cluster for caching, session management, rate limiting',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P0',
    complexity: 'Medium',
    category: 'Infrastructure',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-terraform',
    estimatedDays: 3,
    estimatedHours: 24,
    llmAdjustedHours: 16,
    dependencies: 'INF-006',
    skillsRequired: 'Terraform/AWS',
    phase: '1',
    deliverables: 'terraform/modules/elasticache',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-01-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
  },
  {
    id: 'INF-010',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'MSK Kafka Module',
    description: 'Managed Kafka cluster for event streaming with proper security',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P0',
    complexity: 'High',
    category: 'Infrastructure',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-terraform',
    estimatedDays: 4,
    estimatedHours: 32,
    llmAdjustedHours: 24,
    dependencies: 'INF-006',
    skillsRequired: 'Terraform/AWS',
    phase: '1',
    deliverables: 'terraform/modules/msk',
    notes: '',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-01-15T00:00:00Z',
    updatedAt: '2026-01-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
  },
];

// Continue adding more tasks... (truncated for brevity, but you would add all 300+ tasks from the CSV)

// Payment Gateway Sprint 1 - Tasks
export const paymentGatewaySprintTasks: Task[] = [
  {
    id: 'PAY-001',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Stripe Payment Gateway Integration - Setup',
    description: 'Set up Stripe API integration, configure webhook endpoints, implement payment intent creation',
    type: 'Feature',
    status: 'In Progress',
    priority: 'P0',
    complexity: 'High',
    category: 'Payment Integration',
    assigneeIds: ['user-1'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-payment-service',
    estimatedDays: 5,
    estimatedHours: 40,
    llmAdjustedHours: 32,
    dependencies: '',
    skillsRequired: 'Backend/Go, Stripe API',
    phase: '2',
    deliverables: 'Stripe integration module, webhook handlers',
    notes: 'Need Stripe API keys and test credentials',
    timeLogs: [
      {
        id: 'time-pay-001-1',
        taskId: 'PAY-001',
        userId: 'user-1',
        hours: 8,
        description: 'Set up Stripe SDK and initial configuration',
        date: '2026-02-20',
      },
    ],
    standupNotes: [],
    createdAt: '2026-02-18T00:00:00Z',
    updatedAt: '2026-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [
      {
        id: 'att-pay-001-1',
        name: 'Stripe API Invoice - Feb 2026.pdf',
        url: '/attachments/stripe-invoice-feb-2026.pdf',
        type: 'application/pdf',
        uploadedAt: '2026-02-20T00:00:00Z',
        uploadedBy: 'user-1',
        category: 'invoice',
        metadata: {
          amount: 5000,
          vendor: 'Stripe Inc.',
          invoiceNumber: 'INV-2026-02-001',
          paymentMethod: 'Corporate Credit Card',
          hardCopyLocation: 'Finance Cabinet - Drawer 2 - Folder "Vendors S-T"',
          quickBooksId: 'QB-INV-12345',
          quickBooksStatus: 'synced',
          quickBooksSyncedAt: '2026-02-20T10:30:00Z',
        },
      },
      {
        id: 'att-pay-001-2',
        name: 'Stripe Setup Receipt.pdf',
        url: '/attachments/stripe-setup-receipt.pdf',
        type: 'application/pdf',
        uploadedAt: '2026-02-20T00:00:00Z',
        uploadedBy: 'user-1',
        category: 'receipt',
        metadata: {
          amount: 500,
          vendor: 'Stripe Inc.',
          receiptNumber: 'RCP-2026-02-001',
          paymentMethod: 'Wire Transfer',
          hardCopyLocation: 'Finance Cabinet - Drawer 2 - Folder "Vendors S-T"',
          quickBooksId: 'QB-RCP-12346',
          quickBooksStatus: 'synced',
          quickBooksSyncedAt: '2026-02-20T10:35:00Z',
        },
      },
    ],
    verificationLinks: [],
    externalLinks: [
      { type: 'Stripe Dashboard', url: 'https://dashboard.stripe.com' },
    ],
    cost: 5500,
    changeLogs: [],
  },
  {
    id: 'PAY-002',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'PayPal Payment Gateway Integration',
    description: 'Integrate PayPal Express Checkout and REST API for payment processing',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P1',
    complexity: 'High',
    category: 'Payment Integration',
    assigneeIds: ['user-1'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-payment-service',
    estimatedDays: 4,
    estimatedHours: 32,
    llmAdjustedHours: 28,
    dependencies: 'PAY-001',
    skillsRequired: 'Backend/Go, PayPal API',
    phase: '2',
    deliverables: 'PayPal integration module',
    notes: 'Requires PayPal Business account verification',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-18T00:00:00Z',
    updatedAt: '2026-02-18T00:00:00Z',
    customerVisible: false,
    attachments: [
      {
        id: 'att-pay-002-1',
        name: 'PayPal Developer License Invoice.pdf',
        url: '/attachments/paypal-license-invoice.pdf',
        type: 'application/pdf',
        uploadedAt: '2026-02-18T00:00:00Z',
        uploadedBy: 'user-2',
        category: 'invoice',
        metadata: {
          amount: 2500,
          vendor: 'PayPal Holdings Inc.',
          invoiceNumber: 'PP-INV-2026-001',
          paymentMethod: 'Corporate Credit Card',
          hardCopyLocation: 'Finance Cabinet - Drawer 2 - Folder "Vendors P-R"',
          quickBooksId: 'QB-INV-12347',
          quickBooksStatus: 'pending',
        },
      },
    ],
    verificationLinks: [],
    externalLinks: [
      { type: 'PayPal Developer', url: 'https://developer.paypal.com' },
    ],
    cost: 2500,
    changeLogs: [],
  },
  {
    id: 'PAY-003',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Payment Gateway Abstraction Layer',
    description: 'Create unified interface for multiple payment gateways with factory pattern',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P0',
    complexity: 'Medium',
    category: 'Backend Development',
    assigneeIds: ['user-1'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-payment-service',
    estimatedDays: 3,
    estimatedHours: 24,
    llmAdjustedHours: 20,
    dependencies: 'PAY-001,PAY-002',
    skillsRequired: 'Backend/Go, Design Patterns',
    phase: '2',
    deliverables: 'Payment gateway interface and factory',
    notes: 'Should support easy addition of new payment methods',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-18T00:00:00Z',
    updatedAt: '2026-02-18T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PAY-004',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'QuickBooks Integration - API Setup',
    description: 'Set up QuickBooks Online API integration for automatic invoice and expense syncing',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P1',
    complexity: 'High',
    category: 'Integration',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-payment-service',
    estimatedDays: 6,
    estimatedHours: 48,
    llmAdjustedHours: 40,
    dependencies: '',
    skillsRequired: 'Backend/Go, QuickBooks API, OAuth',
    phase: '2',
    deliverables: 'QuickBooks integration service',
    notes: 'Need QuickBooks Developer credentials and OAuth setup',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-18T00:00:00Z',
    updatedAt: '2026-02-18T00:00:00Z',
    customerVisible: false,
    attachments: [
      {
        id: 'att-pay-004-1',
        name: 'QuickBooks Enterprise License.pdf',
        url: '/attachments/quickbooks-license.pdf',
        type: 'application/pdf',
        uploadedAt: '2026-02-18T00:00:00Z',
        uploadedBy: 'user-2',
        category: 'invoice',
        metadata: {
          amount: 15000,
          vendor: 'Intuit Inc.',
          invoiceNumber: 'QB-ENT-2026-001',
          paymentMethod: 'Wire Transfer',
          hardCopyLocation: 'Finance Cabinet - Drawer 1 - Folder "Major Software Licenses"',
          quickBooksId: 'QB-INV-12348',
          quickBooksStatus: 'synced',
          quickBooksSyncedAt: '2026-02-18T15:00:00Z',
        },
      },
    ],
    verificationLinks: [],
    externalLinks: [
      { type: 'QuickBooks API', url: 'https://developer.intuit.com' },
    ],
    cost: 15000,
    changeLogs: [],
  },
  {
    id: 'PAY-005',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Invoice Management System',
    description: 'Build invoice generation, tracking, and management system with PDF generation',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P1',
    complexity: 'High',
    category: 'Backend Development',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-payment-service',
    estimatedDays: 7,
    estimatedHours: 56,
    llmAdjustedHours: 45,
    dependencies: 'PAY-004',
    skillsRequired: 'Backend/Go, PDF Generation',
    phase: '2',
    deliverables: 'Invoice management service with PDF templates',
    notes: 'Should sync with QuickBooks automatically',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-18T00:00:00Z',
    updatedAt: '2026-02-18T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'PAY-006',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Receipt Storage & Hard Copy Tracking',
    description: 'Build system for digital receipt storage with hard copy location tracking',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P2',
    complexity: 'Medium',
    category: 'Backend Development',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-payment-service',
    estimatedDays: 4,
    estimatedHours: 32,
    llmAdjustedHours: 28,
    dependencies: 'PAY-005',
    skillsRequired: 'Backend/Go, File Storage',
    phase: '2',
    deliverables: 'Receipt management system',
    notes: 'Include OCR for automatic data extraction from receipt images',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-18T00:00:00Z',
    updatedAt: '2026-02-18T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
];

// PoC Sprint - Tasks
export const pocSprintTasks: Task[] = [
  {
    id: 'POC-001',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Transaction Data Schema',
    description: 'Design and implement a unified PostgreSQL schema for Transactions, LedgerEntries, and PaymentMethods',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P0',
    complexity: 'Medium',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-payment-service',
    estimatedDays: 2,
    estimatedHours: 16,
    llmAdjustedHours: 10,
    dependencies: '',
    skillsRequired: 'SQL/Go',
    phase: '1',
    deliverables: 'Migration files; ERD',
    notes: 'Crucial for atomic integrity',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'POC-002',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'ISO 8583 Message Simulator',
    description: 'Implement a mock client to build and parse 0100 (Auth) and 0200 (Completion) network messages',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P0',
    complexity: 'High',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-payment-service',
    estimatedDays: 4,
    estimatedHours: 32,
    llmAdjustedHours: 24,
    dependencies: '',
    skillsRequired: 'Go/ISO8583',
    phase: '1',
    deliverables: 'internal/client/simulator',
    notes: 'Bypasses full processor cert for PoC',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'POC-003',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Idempotent Payment API',
    description: 'Build /v1/payments endpoint with idempotency logic to prevent double-charging',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P0',
    complexity: 'Medium',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-payment-service',
    estimatedDays: 3,
    estimatedHours: 24,
    llmAdjustedHours: 16,
    dependencies: 'POC-001',
    skillsRequired: 'Go',
    phase: '1',
    deliverables: 'Payment API handler',
    notes: 'Core gateway logic',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'POC-004',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'NACHA File Generator',
    description: 'Build logic to generate a valid ACH file structure (File/Batch/Entry headers) for ODFI submission',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P0',
    complexity: 'High',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-payment-service',
    estimatedDays: 4,
    estimatedHours: 32,
    llmAdjustedHours: 24,
    dependencies: 'POC-001',
    skillsRequired: 'Go',
    phase: '1',
    deliverables: 'internal/banking/nacha',
    notes: 'Crux of bank-to-bank movement',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'POC-005',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Async State Machine',
    description: 'Implement status transitions: Pending -> Clearing -> Settled (or Returned)',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P0',
    complexity: 'High',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-payment-service',
    estimatedDays: 3,
    estimatedHours: 24,
    llmAdjustedHours: 18,
    dependencies: 'POC-004',
    skillsRequired: 'Go',
    phase: '1',
    deliverables: 'State machine logic',
    notes: 'Addresses the 2-day delay in ACH',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'POC-006',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Double-Entry Engine',
    description: 'Core logic to ensure every transaction has a Debit and Credit entry in the ledger',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P0',
    complexity: 'High',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-payment-service',
    estimatedDays: 4,
    estimatedHours: 32,
    llmAdjustedHours: 24,
    dependencies: 'POC-001',
    skillsRequired: 'Go',
    phase: '1',
    deliverables: 'internal/ledger/engine.go',
    notes: 'Financial accuracy core',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'POC-007',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'HMAC Request Signing',
    description: 'Implement merchant request signature validation (HMAC-SHA256)',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P0',
    complexity: 'Medium',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-payment-service',
    estimatedDays: 2,
    estimatedHours: 16,
    llmAdjustedHours: 10,
    dependencies: '',
    skillsRequired: 'Go/Crypto',
    phase: '1',
    deliverables: 'Auth middleware',
    notes: 'Critical for API security',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'POC-008',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Basic PAN Encryption',
    description: 'Create a Transit service for AES-GCM encryption of PANs before storage/processing',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P0',
    complexity: 'Medium',
    category: 'Backend',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-payment-service',
    estimatedDays: 3,
    estimatedHours: 24,
    llmAdjustedHours: 16,
    dependencies: '',
    skillsRequired: 'Go/Crypto',
    phase: '1',
    deliverables: 'internal/crypto/transit',
    notes: 'PoC for secure data handling',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'POC-009',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'E2E Money Movement Script',
    description: 'A single script to run a CC Auth and an ACH Transfer, then verify ledger balances',
    type: 'Feature',
    status: 'Not Started',
    priority: 'P0',
    complexity: 'Medium',
    category: 'Integration',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-payment-service',
    estimatedDays: 2,
    estimatedHours: 16,
    llmAdjustedHours: 12,
    dependencies: 'POC-003;POC-006',
    skillsRequired: 'Go/Testing',
    phase: '1',
    deliverables: 'e2e_poc_test.go',
    notes: 'The ultimate PoC deliverable',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-23T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
];